import { ApprovalFilterType } from '@/types/api';
import { WORK_APPROVAL_TAB } from '@/constant/url';
import { IndexPages } from '@/constant';

/**
 * 전자결재 목록(WORK > 결재)에 좌측 필터를 지정한 채로 진입시키는 URL 파라미터.
 *
 *   /?tab=work&subtab=approval&approvalFilter=to-approve
 *
 * 결재 지연 알림 메일(PORTAL-13048)의 '결재 목록 전체보기' 처럼 포탈 밖(메일 템플릿·
 * 푸시 payload)에서 하드코딩하는 링크가 쓴다. 그래서 `ApprovalFilterType` 의 숫자 인덱스가
 * 아니라 안정적인 slug 로 계약한다 — enum 에 항목이 끼어들어 순서가 바뀌어도 링크가 살아 있다.
 */
export const APPROVAL_FILTER_QUERY_KEY = 'approvalFilter';

/**
 * slug → 좌측 필터.
 *
 * 내 정보(passportId)만 있으면 조건이 온전히 서는 필터만 싣는다. 다음은 일부러 뺐다 —
 * 딥링크는 화면이 뜨자마자 필터를 걸기 때문에 선행 조회가 끝나기 전이라, 좌측은 선택돼
 * 있는데 목록은 조건이 빠진 채로 나온다:
 *   - '팀원의 작성/협조결재 문서' — 부서 목록(`useGetCombinedDepartment`)이 아직 없어
 *     `authorDepartmentIds`/`approverDepartmentIds` 가 빈 배열이 되고,
 *     `buildApprovalSearchUrl` 이 빈 배열 파라미터를 떨궈 전체 문서와 같아진다.
 *   - '내가 참조자인 문서' — 미열람 카운트 조회가 아직이라 '미열람' 조건이 빠진다.
 * 넣어야 할 때가 오면 slug 만 추가할 게 아니라 `useApprovalFilterLink` 의 대기 조건도
 * 해당 선행 데이터까지 확장해야 한다.
 *
 * '내 필터'는 저장된 필터 id 가 필요해 `favoriteId` 파라미터가 따로 담당한다.
 */
export const APPROVAL_FILTER_SLUG: Record<string, ApprovalFilterType> = {
  bookmark: ApprovalFilterType['즐겨찾기'],
  all: ApprovalFilterType['전체 문서'],
  'as-approver': ApprovalFilterType['내가 결재자인'],
  // 알림 메일이 쓰는 값 — 내가 현재 결재자인 문서
  'to-approve': ApprovalFilterType['결재할 문서'],
  approved: ApprovalFilterType['결재한 문서'],
  'co-approved': ApprovalFilterType['협조결재한 문서'],
  'as-author': ApprovalFilterType['내가 작성자인'],
  'in-progress': ApprovalFilterType['진행중 문서'],
  draft: ApprovalFilterType['임시저장 문서'],
  completed: ApprovalFilterType['완료 문서'],
  rejected: ApprovalFilterType['반려 문서'],
};

/**
 * 쿼리 값을 좌측 필터로 변환한다. 모르는 값이면 undefined —
 * 호출부는 기존 기본 동작(전체 문서)을 그대로 둔다.
 * '즐겨찾기'가 0 이라 결과는 반드시 `undefined` 비교로 판정할 것.
 */
export const parseApprovalFilterSlug = (
  value: string | string[] | undefined,
): ApprovalFilterType | undefined => {
  const slug = Array.isArray(value) ? value[0] : value;
  if (!slug) return undefined;
  // 프로토타입 키('toString' 등)가 필터로 읽히지 않게 자기 키만 본다.
  if (!Object.prototype.hasOwnProperty.call(APPROVAL_FILTER_SLUG, slug)) {
    return undefined;
  }
  return APPROVAL_FILTER_SLUG[slug];
};

/**
 * 모바일 결재함(`/m/approval`)에서 같은 딥링크가 가리키는 탭 위치.
 *
 * PC 는 좌측 필터 한 단계지만 모바일은 상단 탭(`headerKey`) + 하위 탭(`myApprovalKey`)
 * 두 단계라 slug 하나가 좌표 둘로 풀린다.
 *
 * 결재 지연 알림(PORTAL-13048)이 쓰는 `to-approve` 만 싣는다 — 모바일에 대응 탭이 없는
 * slug 까지 넣으면 엉뚱한 탭으로 보내게 된다. 매핑에 없는 slug 는 지금처럼 PC 화면으로
 * 열린다(기존 동작 유지). 탭을 늘릴 땐 여기 좌표만 추가하면 된다.
 */
export type ApprovalMobileTab = {
  /** 상단 탭 — 0 내 결재 / 1 내 작성 / 2 내 참조 / 3 팀 작성 / 4 팀 결재 */
  headerKey: number;
  /** '내 결재' 하위 탭 — 0 결재할 / 1 결재한 / 2 협조결재한 */
  myApprovalKey: number;
};

export const APPROVAL_FILTER_MOBILE_TAB: Record<string, ApprovalMobileTab> = {
  // 내 결재 > 결재할
  'to-approve': { headerKey: 0, myApprovalKey: 0 },
};

/**
 * 쿼리 값을 모바일 결재함 탭 좌표로 변환한다. 모르는 값이면 undefined —
 * 호출부는 리다이렉트하지 말고 기존 경로를 그대로 둘 것.
 */
export const parseApprovalFilterMobileTab = (
  value: string | string[] | undefined,
): ApprovalMobileTab | undefined => {
  const slug = Array.isArray(value) ? value[0] : value;
  if (!slug) return undefined;
  // 프로토타입 키('toString' 등)가 탭으로 읽히지 않게 자기 키만 본다.
  if (!Object.prototype.hasOwnProperty.call(APPROVAL_FILTER_MOBILE_TAB, slug)) {
    return undefined;
  }
  return APPROVAL_FILTER_MOBILE_TAB[slug];
};

/**
 * 결재 목록 딥링크를 모바일 결재함으로 넘겨야 하는지 (PORTAL-14212).
 *
 * 세 조건을 모두 만족할 때만 참이라, 하나라도 어긋나면 기존 PC 동작이 그대로 유지된다.
 * 훅에서 떼어 둔 이유는 조건 자체를 테스트로 묶어두기 위함이다.
 *
 * @param subtab   현재 URL 의 subtab — 결재 목록 딥링크만 대상으로 삼는다.
 * @param rawSlug  approvalFilter 쿼리 값.
 * @param isApp    앱 웹뷰인지. 모바일 웹 브라우저는 제외한다 — 도착 화면에 뒤로가기가 없다.
 */
export const shouldRedirectToMobileApproval = (
  subtab: string | string[] | undefined,
  rawSlug: string | string[] | undefined,
  isApp: boolean,
): boolean => {
  if (!isApp) return false;
  const currentSubtab = Array.isArray(subtab) ? subtab[0] : subtab;
  if (currentSubtab !== WORK_APPROVAL_TAB) return false;
  return parseApprovalFilterMobileTab(rawSlug) !== undefined;
};

/**
 * 알림에 실린 주소가 '결재 목록 딥링크' 인지 보고, 맞으면 라우터로 이동할 경로를 돌려준다.
 *
 * 알림센터는 결재 상세·회의록이 아닌 알림을 전부 새 탭으로 열어 왔다. 결재 지연 알림은
 * 포탈 안의 결재 목록으로 가는 링크라 새 탭을 띄울 이유가 없어, 이 경우에만 같은 탭에서
 * 옮겨 간다. 나머지 알림은 판정이 `null` 이 되어 기존 새 탭 동작 그대로다.
 *
 * 원점(origin)은 보지 않는다 — 알림은 운영 주소를 담고 오는데 보는 사람은 개발·스테이징에
 * 있을 수 있다. 대신 경로가 홈(`/`)이고 결재 목록 파라미터가 온전할 때만 참으로 본다.
 *
 * `tab` 까지 보는 이유는 홈 화면이 그 값으로 화면을 고르기 때문이다 — `subtab` 만 맞고
 * `tab` 이 없으면 기본값인 HOME 에 착지해, 결재 목록으로 보낸다는 이 함수의 약속이 깨진다.
 *
 * @param link 알림에 실린 주소. 주소로 읽을 수 없으면 `null`.
 */
export const getApprovalListDeepLinkPath = (link: string): string | null => {
  let url: URL;
  try {
    url = new URL(link, 'https://portal.fnf.co.kr');
  } catch {
    return null;
  }
  if (url.pathname !== '/') return null;
  if (url.searchParams.get('tab') !== IndexPages.WORK) return null;
  if (url.searchParams.get('subtab') !== WORK_APPROVAL_TAB) return null;
  if (
    parseApprovalFilterSlug(
      url.searchParams.get(APPROVAL_FILTER_QUERY_KEY) ?? undefined,
    ) === undefined
  ) {
    return null;
  }
  return `${url.pathname}${url.search}`;
};
